/**
 * BetaInterestPage — the ADR-098 work queue (staff console), displayed as
 * "Nexa Interest" (ADR-098 Amendment 1, 13 August 2026). The component, file,
 * route (`/admin/beta-interest`), API client and DynamoDB `source` value all
 * keep `beta-interest` — only what a human reads changed.
 *
 * A lead is a WORK ITEM, not an alert. This surface reads the durable interest
 * records, drives their status state machine, launches BOW pre-filled to
 * provision one (flipping it to `provisioned` and linking the created account),
 * and shows the telemetry journey that produced each lead.
 *
 * Built to the locked mock (mockup-beta-queue-and-user-memberships-v2.html, tab
 * 1): stat cards · status filter pills + search · one shared row grid so header
 * and rows align by construction (118 · 260 · 260 · 130 · 240 · 150) · action
 * ICONS, left-aligned.
 */
import { useCallback, useEffect, useState } from 'react';
import { useStaffMe } from '../../hooks/useStaffMe';
import { useToast, toastFromError } from '../../components/ToastProvider';
import { StaffObwModal, type StaffObwProvisionResult } from '../../obw/StaffObwModal';
import {
  listAllBetaInterest,
  transitionBetaInterest,
  getBetaInterestJourney,
  type BetaLead,
  type BetaInterestStats,
  type BetaInterestStatus,
  type BetaJourneyResult,
} from '../../lib/beta-interest-api';
import { useWorkingZone, zoneByCode } from '../../lib/zones';
import { useZoneReadReport } from '../../components/ZoneContext';
import {
  ZoneCell, ZoneFilter, ZoneFanoutWarning, matchesZone,
} from '../../components/ZoneIndicators';
import { civilDayOf, formatClock24 } from '../../lib/time-format';

const STATUS_CHIP: Record<BetaInterestStatus, { label: string; cls: string }> = {
  new: { label: '● New', cls: 'beta-chip-new' },
  contacted: { label: '◐ Contacted', cls: 'beta-chip-contacted' },
  provisioned: { label: '✓ Provisioned', cls: 'beta-chip-prov' },
  declined: { label: '✕ Declined', cls: 'beta-chip-declined' },
};

const FILTERS: { key: string; label: string }[] = [
  { key: 'new', label: 'New' },
  { key: 'contacted', label: 'Contacted' },
  { key: 'provisioned', label: 'Provisioned' },
  { key: 'declined', label: 'Declined' },
  { key: 'all', label: 'All' },
];

// ── icons (inline SVG, matching the mock's action rail) ──────────────────────
const Icon = {
  provision: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
      <line x1="19" y1="8" x2="19" y2="14" /><line x1="22" y1="11" x2="16" y2="11" />
    </svg>
  ),
  phone: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.4-1.2a2 2 0 0 1 2.1-.5c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2z" />
    </svg>
  ),
  decline: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  chevron: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  ),
  open: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  ),
};

/** "28/10 09:14" — the day and 24-hour clock the lead arrived, in the staff member's zone. */
function formatReceived(iso: string): string {
  const day = civilDayOf(iso);
  if (!day) return iso;
  return `${day.slice(8, 10)}/${day.slice(5, 7)} ${formatClock24(iso)}`;
}

export function BetaInterestPage() {
  const me = useStaffMe();
  if (me.status === 'loading') {
    return <main className="portal-page"><p className="portal-muted">Loading…</p></main>;
  }
  if (me.status === 'error' || (me.me.role !== 'super_admin' && me.me.role !== 'nexa_admin')) {
    return (
      <main className="portal-page">
        <h1>Nexa Interest</h1>
        <p className="portal-error" role="alert">
          Nexa Interest is restricted to Nexa Admin and Super Admin roles.
        </p>
      </main>
    );
  }
  return <BetaInterestSurface consultantName={me.me.name ?? ''} />;
}

function BetaInterestSurface({ consultantName }: { consultantName: string }) {
  const toast = useToast();
  const [leads, setLeads] = useState<BetaLead[]>([]);
  const [stats, setStats] = useState<BetaInterestStats | null>(null);
  const [notifyAddress, setNotifyAddress] = useState('');
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('new');
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [journeys, setJourneys] = useState<Record<string, BetaJourneyResult | 'loading'>>({});
  const [provisionLead, setProvisionLead] = useState<BetaLead | null>(null);
  // ADR-124 D5 — the console's working zone (ADR-123 D3 precedence), for the residency step's
  // "This account will be created in {Zone}." notice.
  const workingZone = useWorkingZone();
  // ADR-122 Pattern 1 — the fan-out's partial-read outcome, and the narrowing control.
  const [failedZones, setFailedZones] = useState<string[]>([]);
  const [zoneFilter, setZoneFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listAllBetaInterest({ status: filter, q: search });
      setLeads(res.leads);
      setStats(res.stats);
      setNotifyAddress(res.notifyAddress);
      setFailedZones(res.failedZones);
    } catch (err) {
      const { title, detail } = toastFromError(err, 'Could not load Nexa Interest');
      toast.push({ variant: 'error', title, detail });
    } finally {
      setLoading(false);
    }
  }, [filter, search, toast]);

  useEffect(() => { void load(); }, [load]);

  // ADR-123 Decision 1 — the navigation bar names the zones in this queue and their counts.
  useZoneReadReport({ rows: loading ? null : leads, failedZones, filter: zoneFilter });

  const doTransition = useCallback(
    async (lead: BetaLead, toStatus: BetaInterestStatus) => {
      setBusyId(lead.id);
      try {
        // The lead's OWN zone, from the fan-out tag — not the console's home zone. A
        // us-east-1 lead transitioned against ap-southeast-2 would 404 on a record the
        // operator is looking at, which reads as data loss rather than a misrouted request.
        await transitionBetaInterest(lead.id, toStatus, {}, zoneByCode(lead.zone));
        toast.push({
          variant: 'success',
          title: toStatus === 'contacted' ? 'Marked as contacted' : 'Lead declined',
          detail: `${lead.businessName} → ${toStatus}.`,
        });
        await load();
      } catch (err) {
        const { title, detail } = toastFromError(err, 'Could not update the lead');
        toast.push({ variant: 'error', title, detail });
      } finally {
        setBusyId(null);
      }
    },
    [toast, load],
  );

  const toggleJourney = useCallback(
    async (lead: BetaLead) => {
      if (expandedId === lead.id) { setExpandedId(null); return; }
      setExpandedId(lead.id);
      if (!journeys[lead.id]) {
        setJourneys((j) => ({ ...j, [lead.id]: 'loading' }));
        try {
          const res = await getBetaInterestJourney(lead.id, zoneByCode(lead.zone));
          setJourneys((j) => ({ ...j, [lead.id]: res }));
        } catch (err) {
          const { title, detail } = toastFromError(err, 'Could not load the journey');
          toast.push({ variant: 'error', title, detail });
          setJourneys((j) => { const n = { ...j }; delete n[lead.id]; return n; });
          setExpandedId(null);
        }
      }
    },
    [expandedId, journeys, toast],
  );

  // ADR-098 — a successful BOW provision flips the lead to `provisioned` and
  // links the created account, closing the interest → provisioned → participant
  // loop with no re-typing and no orphan.
  const onProvisioned = useCallback(
    async (result?: StaffObwProvisionResult) => {
      const lead = provisionLead;
      if (!lead) return;
      if (result?.accountId) {
        try {
          await transitionBetaInterest(lead.id, 'provisioned', {
            linkedAccountId: result.accountId,
            linkedCustomerNumber: result.customerNumber,
          }, zoneByCode(lead.zone));
        } catch (err) {
          const { title, detail } = toastFromError(err, 'Provisioned, but could not link the lead');
          toast.push({ variant: 'error', title, detail });
        }
      }
      await load();
    },
    [provisionLead, toast, load],
  );

  // The zone filter narrows what is RENDERED; the stat cards above deliberately keep reporting
  // every zone, because they answer "how is the programme doing" rather than "what am I
  // looking at". Narrowing both would make the filter silently change the numbers a reader
  // might be quoting elsewhere.
  const visibleLeads = leads.filter((l) => matchesZone(l, zoneFilter));

  const flowValidatorHref = (sessionToken: string) =>
    `/admin/diags?tab=flow&session=${encodeURIComponent(sessionToken)}`;

  return (
    <main className="portal-page portal-beta-interest">
      <h1>Nexa Interest</h1>
      <p className="portal-muted portal-beta-sub">
        COG → Nexa Interest · subscriptions gate closed — every signup lands here as a lead, not an account.
      </p>

      {notifyAddress && (
        <div className="portal-beta-notify">
          <span className="portal-beta-notify-dot" aria-hidden="true" />
          <span className="portal-beta-notify-txt">
            A staff email is sent on every new interest, plus a daily digest at 08:00 ACST.
          </span>
          <span className="portal-beta-notify-cfg">Notifications → {notifyAddress}</span>
        </div>
      )}

      {stats && (
        <div className="portal-beta-stats">
          <StatCard label="New" value={stats.new} sub="awaiting first contact" color="#4ade80" />
          <StatCard
            label="Contacted" value={stats.contacted}
            sub={stats.oldestWaitingDays > 0 ? `oldest waiting ${stats.oldestWaitingDays} day${stats.oldestWaitingDays === 1 ? '' : 's'}` : 'none waiting'}
            color="#fbbf24"
          />
          <StatCard label="Provisioned" value={stats.provisioned} sub="now in Beta Participants" color="#c7d2fe" />
          <StatCard label="Conversion" value={`${stats.conversion}%`} sub="interest → provisioned" />
        </div>
      )}

      <div className="portal-beta-toolbar">
        {FILTERS.map((f) => {
          const n = stats
            ? f.key === 'all' ? stats.total : (stats[f.key as keyof BetaInterestStats] as number)
            : 0;
          return (
            <button
              key={f.key}
              type="button"
              className={`portal-beta-pill${filter === f.key ? ' on' : ''}`}
              onClick={() => setFilter(f.key)}
            >
              {f.label} <span className="portal-beta-pill-n">{n}</span>
            </button>
          );
        })}
        <input
          className="portal-beta-search"
          placeholder="Search business, website, email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search Nexa Interest leads"
        />
        <ZoneFilter value={zoneFilter} onChange={setZoneFilter} className="portal-beta-zonefilter" />
      </div>

      {/* ⭐⭐ A ZONE THAT DID NOT ANSWER CONTRIBUTES NO LEADS, and a short queue looks exactly
          like a complete one. On THIS page that is the sharpest version of the problem in the
          whole ADR: the sign-up gate is closed, so every sign-up attempt on the platform lands
          here — an absent zone means staff never learn those people tried to sign up. */}
      <ZoneFanoutWarning failedZones={failedZones} noun="leads" onRetry={() => void load()} />

      <div className="portal-beta-tbl">
        <div className={`portal-beta-th is-multizone`}>
          <div>Received</div>
          <div>Zone</div>
          <div>Business</div><div>Contact</div>
          <div>Status</div><div>How they found you</div><div>Actions</div>
        </div>

        {loading && <div className="portal-beta-empty">Loading…</div>}
        {!loading && visibleLeads.length === 0 && (
          <div className="portal-beta-empty">No leads {filter === 'all' ? 'yet' : `in “${filter}”`}.</div>
        )}

        {!loading && visibleLeads.map((lead) => {
          const chip = STATUS_CHIP[lead.status];
          const isProvisioned = lead.status === 'provisioned';
          const isDeclined = lead.status === 'declined';
          const journey = journeys[lead.id];
          return (
            <div key={lead.id}>
              <div className={`portal-beta-tr${lead.status === 'new' ? ' is-new' : ''} is-multizone`}>
                <div className="portal-beta-when">{formatReceived(lead.capturedAt)}</div>
                <div className="portal-beta-zone"><ZoneCell zone={lead.zone} /></div>
                <div>
                  <div className="portal-beta-biz">{lead.businessName || '—'}</div>
                  {lead.domain && <div className="portal-beta-site">{lead.domain}</div>}
                </div>
                <div>
                  <div className="portal-beta-who">{lead.ownerName || '—'}</div>
                  <div className="portal-beta-mail">{lead.email}</div>
                </div>
                <div><span className={`portal-beta-chip ${chip.cls}`}>{chip.label}</span></div>
                <div className="portal-beta-src">
                  {lead.hasJourney ? (
                    <button type="button" className="portal-beta-src-btn" onClick={() => void toggleJourney(lead)}>
                      Journey captured
                    </button>
                  ) : (
                    <span className="portal-beta-src-none">No journey captured</span>
                  )}
                </div>
                <div className="portal-beta-acts">
                  {!isProvisioned && !isDeclined && (
                    <>
                      <button
                        type="button" className="portal-beta-ico primary" disabled={busyId === lead.id}
                        title="Provision beta account (launches BOW, pre-filled)"
                        onClick={() => setProvisionLead(lead)}
                      >{Icon.provision}</button>
                      {lead.status !== 'contacted' && (
                        <button
                          type="button" className="portal-beta-ico" disabled={busyId === lead.id}
                          title="Mark contacted" onClick={() => void doTransition(lead, 'contacted')}
                        >{Icon.phone}</button>
                      )}
                      <button
                        type="button" className="portal-beta-ico danger" disabled={busyId === lead.id}
                        title="Decline" onClick={() => void doTransition(lead, 'declined')}
                      >{Icon.decline}</button>
                    </>
                  )}
                  {isProvisioned && lead.linkedAccountId && (
                    <a
                      className="portal-beta-ico" title="Open account"
                      href={`/admin/customers/${encodeURIComponent(lead.linkedAccountId)}`}
                    >{Icon.open}</a>
                  )}
                  <button
                    type="button"
                    className={`portal-beta-ico${expandedId === lead.id ? ' on' : ''}`}
                    title="Show the journey that produced the lead"
                    disabled={!lead.hasJourney}
                    onClick={() => void toggleJourney(lead)}
                  >{Icon.chevron}</button>
                </div>
              </div>

              {expandedId === lead.id && (
                <div className="portal-beta-journey">
                  <div className="portal-beta-journey-hd">The journey that produced this lead</div>
                  {journey === 'loading' && <div className="portal-muted">Resolving journey…</div>}
                  {journey && journey !== 'loading' && !journey.captured && (
                    <div className="portal-muted">
                      No journey captured for this lead — it predates journey attachment, or the visitor
                      session was not linked at submit.
                    </div>
                  )}
                  {journey && journey !== 'loading' && journey.captured && journey.session && (
                    <>
                      <div className="portal-beta-flow">
                        {journey.session.steps
                          .filter((s) => s.eventType === 'pageview' || s.eventType === 'journey' || s.isEntry)
                          .map((s, i, arr) => (
                            <span key={`${s.timestamp}-${i}`} className="portal-beta-flow-item">
                              <span className={`portal-beta-st${s.isEntry ? ' entry' : ''}${s.journeyStep.includes('submit') ? ' win' : ''}`}>
                                {s.journeyStep || s.pagePath || s.eventType}
                                {s.dwellSeconds > 0 ? ` (${s.dwellSeconds}s)` : ''}
                              </span>
                              {i < arr.length - 1 && <span className="portal-beta-arr">→</span>}
                            </span>
                          ))}
                      </div>
                      <div className="portal-beta-jmeta">
                        Session <code>{journey.sessionToken}</code> · {journey.session.source || 'direct'} ·{' '}
                        {journey.session.pageCount} page{journey.session.pageCount === 1 ? '' : 's'} ·{' '}
                        <a href={flowValidatorHref(journey.sessionToken)}>Open in Flow Validator ↗</a>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {provisionLead && (
        <StaffObwModal
          journeyId="bow"
          // ⭐ ADR-124 D4 — THE LEAD ALREADY SAID WHERE IT IS REGISTERED. A lead captured after
          // the residency step carries its declared country, and the BOW's residency step is
          // PRE-FILLED with it ("without asking twice") — the consultant confirms, and the zone
          // follows from the country, exactly as on every other create. A lead that predates the
          // step carries none, and the consultant asks the customer; nothing is guessed.
          residencyPrefill={provisionLead.jurisdiction || undefined}
          workingZone={workingZone}
          consultantName={consultantName}
          leadPrefill={{
            businessName: provisionLead.businessName,
            domainUrl: provisionLead.domain,
            ownerName: provisionLead.ownerName,
            email: provisionLead.email,
          }}
          onProvisioned={onProvisioned}
          onClose={() => setProvisionLead(null)}
        />
      )}
    </main>
  );
}

function StatCard({ label, value, sub, color }: { label: string; value: number | string; sub: string; color?: string }) {
  return (
    <div className="portal-beta-stat">
      <div className="portal-beta-stat-lbl">{label}</div>
      <div className="portal-beta-stat-val" style={color ? { color } : undefined}>{value}</div>
      <div className="portal-beta-stat-sub">{sub}</div>
    </div>
  );
}
